<template>
  <div class="dataTable">
    <project-toolbar>
      <md-menu
        v-if="usageDailyData.length"
        md-align-trigger
        md-size="auto"
        class="box-right-edge"
      >
        <md-button md-menu-trigger>{{ L('download') }}</md-button>
        <md-menu-content>
          <md-menu-item
            v-for="(_, page) in Array(totalDownloadPage)"
            @click="download(page)"
            :key="page"
            >{{ L('download') + (page + 1) }}</md-menu-item
          >
        </md-menu-content>
      </md-menu>
      <md-field v-if="usageDailyData.length">
        <md-select v-model="checkedHeaders" multiple class="selectFields">
          <md-option
            v-for="header in allHeaders"
            :value="header"
            :key="header"
            >{{ L(snakeToCamelCase(header)) }}</md-option
          >
        </md-select>
      </md-field>
      <md-field style="min-width: 240px">
        <label>{{ L('searchBy') }}</label>
        <md-select v-model="searchTypeInput">
          <md-option
            v-for="searchTypeInput in searchTypeList"
            :value="searchTypeInput"
            :key="searchTypeInput"
            >{{ L(snakeToCamelCase(searchTypeInput)) }}</md-option
          >
        </md-select>
        <md-input
          v-model="searchInput"
          :placeholder="placeholder"
          @keyup.enter="updateUsageDailyData"
        />
      </md-field>
      <md-menu>
        <md-button class="account-btn" md-menu-trigger>{{
          L(dateType)
        }}</md-button>
        <md-menu-content>
          <md-menu-item
            :class="'presetList'"
            @click="setMonthType('oneMonth')"
            >{{ L('oneMonth') }}</md-menu-item
          >
          <md-menu-item
            :class="'presetList'"
            @click="setMonthType('threeMonths')"
            >{{ L('threeMonths') }}</md-menu-item
          >
          <md-menu-item
            :class="'presetList'"
            @click="setMonthType('sixMonths')"
            >{{ L('sixMonths') }}</md-menu-item
          >
          <md-menu-item
            :class="'presetList'"
            @click="setMonthType('oneYear')"
            >{{ L('oneYear') }}</md-menu-item
          >
          <md-menu-item :class="'presetList'" @click="setMonthType('range')">
            {{ L('range') }}</md-menu-item
          >
        </md-menu-content>
      </md-menu>
      <md-menu
        md-align-trigger
        md-size="small"
        v-show="useSelectBox"
        class="sub-toolbar-last-menu"
      >
        <md-button class="account-btn" md-menu-trigger
          >{{ months.join('~') }}<fa-icon icon="caret-down"
        /></md-button>
        <md-menu-content>
          <md-menu-item
            v-for="month in monthArray"
            :key="month"
            class="account-item"
            @click="changeMonth(month)"
            >{{ month }}</md-menu-item
          >
        </md-menu-content>
      </md-menu>
      <md-datepicker
        :md-disabled-dates="disableStrDates()"
        v-model="pickupStartDate"
        v-show="!useSelectBox"
        :class="'datePicker'"
      >
      </md-datepicker>
      <md-datepicker
        :md-disabled-dates="disableEndDates()"
        v-model="pickupEndDate"
        v-show="!useSelectBox"
        :class="'datePicker'"
      >
      </md-datepicker>
    </project-toolbar>
    <md-empty-state v-if="!usageDailyData.length" :md-label="C('noData')" />
    <md-content v-if="usageDailyData.length">
      <md-table md-stripe>
        <draggable v-model="checkedHeaders" tag="tr" class="draggable">
          <md-table-head v-for="header in checkedHeaders" :key="header">{{
            L(snakeToCamelCase(header))
          }}</md-table-head>
        </draggable>
        <md-table-row
          slot="md-table-row"
          v-for="(item, idx) in usageDailyData"
          :key="idx"
        >
          <md-table-cell v-for="(header, index) in checkedHeaders" :key="index">
            {{ item[header] }}
          </md-table-cell>
        </md-table-row>
      </md-table>
      <div
        class="data-footer"
        v-if="usageDailyData.length"
        style="font-size: 13px"
      >
        <span>Rows per page:</span>
        <md-field class="limitFields">
          <md-select v-model="limit">
            <md-option v-for="limit in limitList" :value="limit" :key="limit">{{
              limit
            }}</md-option>
          </md-select>
        </md-field>
        <div class="pagination">
          {{ curElements }}-{{ endElements }} of&nbsp;{{ totalCount }}
        </div>
        <md-button
          class="md-icon-button md-dense"
          @click="prevPage"
          :disabled="curPage === 0"
          ><md-icon class="md-size-1x">chevron_left</md-icon></md-button
        >
        <md-button
          class="md-icon-button md-dense"
          @click="nextPage"
          :disabled="curPage + 1 === totalPage"
          ><md-icon class="md-size-1x">chevron_right</md-icon></md-button
        >
      </div>
    </md-content>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import api from '@/api'
import moment from 'moment'
import draggable from 'vuedraggable'
import { getDate, getUserProjectPermissions } from '@/lib/utils'
import XLSX from 'xlsx'
import ProjectToolbar from './ProjectToolbar'

export default {
  components: { ProjectToolbar, draggable },

  data: () => ({
    curElements: 0,
    endElements: 0,
    limitList: [50, 100, 250, 500, 1000],
    limit: 100,
    curPage: 0,
    totalCount: 0,
    totalPage: 0,
    totalDownloadPage: 0,
    downloadLimit: 300000,
    searchInput: '',
    searched: '',
    searchTypeInput: 'project_id',
    searchType: '',
    searchTypeList: ['project_id', 'service_id', 'sku_id'],
    defaultHeaders: [
      'project_id',
      'project_name',
      'service_id',
      'service_name',
      'sku_id',
      'sku_name',
      'invoice_month',
      'usage_unit',
      'usage_date',
      'usage',
      'final_cost',
    ],
    checkedHeaders: [],
    allHeaders: [
      'project_id',
      'project_name',
      'service_id',
      'service_name',
      'sku_id',
      'sku_name',
      'invoice_month',
      'usage_unit',
      'usage_date',
      'usage',
      'final_cost',
      'region',
      'zone',
      'cost_type',
      'location_country',
      'promotion',
      'committed_usage_discount',
      'sustained_usage_discount',
      'free_tier',
      'spending_based_discount',
      't_system_machine_spec',
      't_system_cores',
      't_system_memory',
      'currency',
      'currency_conversion_rate',
    ],
    usageDailyData: [],
    useSelectBox: false,
    monthArray: [],
    pickupStartDate: null,
    pickupEndDate: null,
    months: [],
    dateType: 'oneMonth',
    userProjectPermissions: [],

    disableStrDates() {
      return (date) => {
        const from = moment(date)
        const oneYear = moment(this.pickupEndDate).subtract(1, 'year')
        if (this.pickupEndDate && !this.pickupStartDate) {
          const selectDate = moment(this.pickupEndDate)
          return oneYear.isAfter(from) || !selectDate.isAfter(from)
        }
        if (this.pickupEndDate && this.pickupStartDate) {
          return oneYear.isAfter(from)
        }
        return false
      }
    },

    disableEndDates() {
      return (date) => {
        const from = moment(date)
        if (this.pickupStartDate) {
          const oneYear = moment(this.pickupStartDate).add(1, 'year')
          const selectDate = moment(this.pickupStartDate)
          return oneYear.isBefore(from) || !selectDate.isBefore(from)
        }
        return false
      }
    },
  }),

  mounted() {
    this.setMonthType(this.dateType)
    this.load()
    this.checkedHeaders = [...this.defaultHeaders]
    $('.datePicker').find('input').attr('disabled', 'disabled')
  },

  computed: {
    ...mapState({
      curAccount: (state) => state.curAccount,
      userInfo: (state) => state.userInfo,
    }),

    curAccountId() {
      return this.curAccount.id
    },

    placeholder() {
      return `${this.L(this.snakeToCamelCase(this.searchTypeInput))}...`
    },
  },

  watch: {
    limit() {
      this.resetPage()
      this.loadUsageDailyData()
    },

    curAccount() {
      this.load()
    },

    months(_, oldDate) {
      if (oldDate.length === 0) return
      this.load()
    },

    pickupStartDate(_, oldDate) {
      if (!oldDate) return
      this.changeRangeDate()
    },

    pickupEndDate(_, oldDate) {
      if (!oldDate) return
      this.changeRangeDate()
    },
  },

  methods: {
    async load() {
      if (!(this.curAccountId && this.months.length)) return
      this.searchInput = ''
      this.userProjectPermissions = getUserProjectPermissions(
        this.userInfo.resource,
        this.curAccount
      )
      this.updateUsageDailyData()
    },

    async updateUsageDailyData() {
      this.searchType = this.searchTypeInput
      this.searched = this.searchInput
      this.totalCount = await this.getTotalCount()
      if (this.totalCount === 0) {
        this.usageDailyData = []
        return
      }
      this.resetPage()
      await this.loadUsageDailyData()
    },

    async getTotalCount() {
      const params = {
        searchType: this.searchType,
        search: this.searched,
        ...this.getDateType(),
        projects: this.userProjectPermissions,
      }
      const [{ count: totalCount }] = await api.usage.usageDailyCount(
        this.curAccountId,
        params
      )
      return Number(totalCount)
    },

    resetPage() {
      this.curPage = 0
      this.curElements = 1
      this.endElements =
        this.totalCount < this.limit ? this.totalCount : this.limit
    },

    async loadUsageDailyData() {
      const params = {
        offset: this.curPage * this.limit,
        limit: this.limit,
        ...this.getDateType(),
        searchType: this.searchType,
        search: this.searched,
        projects: this.userProjectPermissions,
      }

      this.usageDailyData = await api.usage.usageDaily(
        this.curAccountId,
        params
      )
      if (this.usageDailyData.length === 0) return

      this.totalPage = Math.ceil(this.totalCount / this.limit)

      this.totalDownloadPage = Math.ceil(this.totalCount / this.downloadLimit)
    },

    getDateType() {
      if (this.dateType === 'range') {
        const endDate = this.pickupEndDate
          ? moment(this.pickupEndDate).format('YYYYMMDD')
          : moment(this.months[1]).endOf('month').format('YYYYMMDD')
        const startDate = this.pickupStartDate
          ? moment(this.pickupStartDate).format('YYYYMMDD')
          : moment(this.months[0] || endDate)
              .startOf('month')
              .format('YYYYMMDD')
        return { startDate, endDate }
      }

      const period = {
        oneMonth: 1,
        threeMonths: 3,
        sixMonths: 6,
        oneYear: 12,
      }
      return {
        month:
          this.dateType === 'oneMonth'
            ? this.months[0].toString()
            : this.months[1].toString(),
        period: period[this.dateType],
      }
    },

    setMonthType(dateType) {
      if (dateType === 'range') {
        if (this.dateType === 'range') return

        this.pickupEndDate = null
        this.pickupStartDate = null
        this.useSelectBox = false
        this.changeRangeDate()
        this.dateType = dateType
        this.updateUsageDailyData()
      } else {
        this.pickupEndDate = null
        this.pickupStartDate = null
        this.dateType = dateType
        this.useSelectBox = true
        this.monthArray = getDate(dateType)
        this.changeMonth(this.monthArray[0])
      }
    },

    changeRangeDate() {
      if (this.pickupEndDate === null || this.pickupStartDate === null) {
        const [startMonth, endMonth] = this.months
        this.pickupStartDate = moment(startMonth || endMonth)
          .startOf('month')
          .format('YYYY-MM-DD')
        this.pickupEndDate = moment(endMonth)
          .endOf('month')
          .format('YYYY-MM-DD')
        return
      }
      const to = moment(this.pickupStartDate)
      const year = moment(this.pickupStartDate).add(1, 'year')
      const from = moment(this.pickupEndDate)

      if ((!from.isBefore(to) && to.isBefore(year)) || from.isSame(year)) {
        this.months = [
          moment(this.pickupEndDate).format('YYYY-MM-DD'),
          moment(this.pickupStartDate).format('YYYY-MM-DD'),
        ]
        this.monthArray = []
      }
    },

    changeMonth(month) {
      this.months =
        this.dateType === 'range'
          ? [
              moment(this.pickupEndDate).format('YYYY-MM-DD'),
              moment(this.pickupStartDate).format('YYYY-MM-DD'),
            ]
          : month.split('~')
    },

    async download(page) {
      const entireUsageDailyData = (
        await this.entireUsageDailyData(page)
      ).reduce(
        (arr, dailyData) => {
          const row = this.checkedHeaders.map((key) => dailyData[key])
          arr.push(row)
          return arr
        },
        [this.checkedHeaders.map((h) => h.toUpperCase())]
      )
      const workBook = XLSX.utils.book_new()

      XLSX.utils.book_append_sheet(
        workBook,
        XLSX.utils.aoa_to_sheet(entireUsageDailyData),
        this.months[0]
      )
      XLSX.writeFile(
        workBook,
        `Cost Table_${this.curAccount.name}_${this.months[0]}.csv`
      )
    },

    async entireUsageDailyData(page) {
      const params = {
        columns: this.checkedHeaders,
        offset: page * this.downloadLimit,
        limit: this.downloadLimit,
        searchType: this.searchType,
        search: this.searched,
        ...this.getDateType(),
        projects: this.userProjectPermissions,
      }

      return api.usage.usageDaily(this.curAccountId, params)
    },

    async prevPage() {
      this.curPage -= 1
      this.curElements -= this.limit
      if (this.curPage === this.totalPage - 2) {
        this.endElements = this.curElements - 1 + this.limit
      } else {
        this.endElements -= this.limit
      }
      await this.loadUsageDailyData()
    },

    async nextPage() {
      this.curPage += 1
      this.curElements += this.limit
      if (this.curPage + 1 === this.totalPage) {
        this.endElements = this.totalCount
      } else {
        this.endElements += this.limit
      }
      await this.loadUsageDailyData()
    },

    snakeToCamelCase(str) {
      return str.replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase())
    },
  },
}
</script>

<style lang="scss" scoped>
.draggable {
  cursor: pointer;
}

.limitFields {
  max-width: 5%;
  margin-left: 20px;
}

.data-footer {
  bottom: 0;
  height: 50px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  z-index: 100;
}

.pagination {
  margin-left: 30px;
  margin-right: 20px;
}

.md-toolbar .md-field {
  margin-top: -3px;
  margin-bottom: 1px;
  margin-left: 10px;
  padding-top: 0px;
  align-items: flex-end;
}

.md-field {
  min-height: 42px;
  width: 40%;
}

.md-content {
  padding: 3px;
  padding-bottom: 0;
}

.datePicker {
  width: 120px;
  min-height: 0px;
  max-height: 30px;
  top: 5px;
  padding: 0;
  margin: 0;
}

.datePicker .md-input {
  height: auto;
  font-size: 12px;
  line-height: 0px;
}

.selectFields {
  width: 250px;
}

.md-input {
  width: 120px;
}
</style>

<style lang="scss">
@import '../../assets/scss/pivot.scss';

.datePicker button {
  display: none;
}

.md-datepicker-body-content {
  height: 250px !important;
}

.md-datepicker-dialog {
  @media (min-width: 601px) {
    left: calc(100% - 480px) !important;
  }
}
</style>
